import * as React from 'react';
import { DisplayDate } from 'owa-datetime';
import { AgendaEventViewBase, Detail, AgendaEventViewBaseProps } from 'owa-agenda-event-views';
import { getDurationString } from '../selectors/userConfigSelectors';
import { formatUserTime } from '../selectors/dateTimeSelectors';
import { observer } from 'mobx-react';
import { getCalendarEventIcons } from 'owa-calendar-event-icons';

const styles = require('./AgendaEventViewNormal.scss');
const classNames = require('classnames/bind').bind(styles);

export interface NormalProps {
    startTime: DisplayDate;
    endTime: DisplayDate;
    organizerEmail: string;
    organizerName: string;
    location: string;
    subject: string;
    isCancelled: boolean;
    isMeeting: boolean;
    hasAttachments: boolean;
    isRecurring: boolean;
    isPrivate: boolean;
    isRecurringException: boolean;
    charmId: number;
}

export type AgendaEventViewNormalProps = Omit<
    AgendaEventViewBaseProps,
    'details' | 'timeDetails' | 'icons'
> &
    NormalProps;

function withNormalView(
    AgendaEventViewBase: React.ComponentClass<AgendaEventViewBaseProps>
): React.ComponentType<AgendaEventViewNormalProps> {
    const AgendaEventViewNormal = observer(
        class extends React.Component<AgendaEventViewBaseProps & NormalProps> {
            render() {
                const {
                    startTime,
                    endTime,
                    isCancelled,
                    subject,
                    location,
                    className,
                    organizerName,
                    isMeeting,
                    hasAttachments,
                    isRecurring,
                    isPrivate,
                    isRecurringException,
                    charmId,
                    ...passThroughProps
                } = this.props;
                const details: Detail[] = [];

                details.push({
                    getTextProps: () => ({
                        text: subject,
                        className: classNames(styles.subject, { isCancelled: isCancelled }),
                    }),
                    getCharmControllProps: charmId
                        ? () => ({ iconId: charmId, className: styles.charm })
                        : null,
                });
                if (location) {
                    details.push({ getTextProps: () => ({ text: location }) });
                }

                if (!location && isMeeting && organizerName) {
                    details.push({ getTextProps: () => ({ text: organizerName }) });
                }

                const timeDetails = [
                    {
                        getTextProps: () => ({
                            text: formatUserTime(startTime),
                            className: styles.timePrimaryText,
                        }),
                    },
                    {
                        getTextProps: () => ({
                            text: getDurationString(startTime, endTime),
                            className: styles.timeSecondaryText,
                        }),
                    },
                ];

                return (
                    <AgendaEventViewBase
                        {...passThroughProps}
                        details={details}
                        timeDetails={timeDetails}
                        icons={getCalendarEventIcons(
                            hasAttachments,
                            isRecurring,
                            isPrivate,
                            isRecurringException
                        )}
                        className={className}
                    />
                );
            }
        }
    );

    return AgendaEventViewNormal;
}

export const AgendaEventViewNormal = withNormalView(AgendaEventViewBase);
